import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useFragment } from "react-relay";

import {
  Box,
  HStack,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";
import { useFormatClubCurrency } from "@whoosh/features/src/ClubCurrency";

import { getEventSignupCheckoutState } from "../hooks/useEventSignupCheckoutState";
import { EventSignupSummaryTotal_signUp$key } from "./__generated__/EventSignupSummaryTotal_signUp.graphql";

type Props = {
  fragmentRef: EventSignupSummaryTotal_signUp$key;
  finalAmountLabel?: string;
};

export const EventSignupSummaryTotal = ({
  fragmentRef,
  finalAmountLabel = t`Pay today`,
}: Props) => {
  const formatPrice = useFormatClubCurrency();
  const signUp = useFragment(EventSignupSummaryTotalFragment, fragmentRef);

  const registrations = signUp?.registrations?.edges ?? [];
  const isPerSignUpEvent = signUp.event?.primaryChargeType === "PER_SIGN_UP";
  const seenChargeTypes = new Set<string>();
  const subtotal = registrations.reduce((sum, edge) => {
    const chargeTypeId = edge?.node?.primaryChargeType?.id ?? null;
    const price =
      edge?.node?.primaryChargeType?.chargeRuleSet?.chargeRules?.edges?.[0]
        ?.node?.price ?? 0;

    if (!isPerSignUpEvent || chargeTypeId === null) {
      return sum + price;
    }

    if (seenChargeTypes.has(chargeTypeId)) {
      return sum;
    }

    seenChargeTypes.add(chargeTypeId);
    return sum + price;
  }, 0);

  const activeOrders =
    signUp?.orders?.edges?.flatMap((edge) => (edge?.node ? [edge.node] : [])) ??
    [];
  const { orderTotal, orderTaxes, total } = getEventSignupCheckoutState({
    orders: activeOrders,
    registrationSubtotal: subtotal,
    isDraftSignUp: signUp.status === "DRAFT",
  });

  const entryFee = orderTotal === null ? subtotal : orderTotal - orderTaxes;
  const taxes = orderTaxes;

  if (entryFee === 0 && taxes === 0 && total === 0) return null;

  return (
    <VStack
      gap={4}
      padding={5}
      backgroundColor="surface-primary"
      testID="event-signup-summary-charges"
    >
      <Text variant="mainHeading">{t`Summary`}</Text>

      <VStack gap={3} paddingBottom={2}>
        <HStack alignItems="center">
          <Text variant="mainBaseMedium" flex={1}>
            {t`Event Entry Fee`}
          </Text>
          <Text variant="mainBaseMedium">{formatPrice(entryFee)}</Text>
        </HStack>

        {taxes > 0 ? (
          <HStack alignItems="center">
            <Text variant="mainBaseMedium" flex={1}>
              {t`Taxes`}
            </Text>
            <Text variant="mainBaseMedium">{formatPrice(taxes)}</Text>
          </HStack>
        ) : null}

        <HStack alignItems="center">
          <Text variant="mainBaseMedium" flex={1}>
            {t`Total`}
          </Text>
          <Text variant="mainBaseMedium">{formatPrice(total)}</Text>
        </HStack>

        <Box
          height={1}
          backgroundColor="surface-dividers"
          width="100%"
          marginVertical={2}
        />

        <HStack alignItems="center">
          <Text variant="mainHeading" flex={1}>
            {finalAmountLabel}
          </Text>
          <Text variant="mainHeading">{formatPrice(total)}</Text>
        </HStack>
      </VStack>
    </VStack>
  );
};

export const EventSignupSummaryTotalFragment = graphql`
  fragment EventSignupSummaryTotal_signUp on EventSignUp {
    status
    event {
      id
      primaryChargeType
    }
    registrations(first: 50) {
      edges {
        node {
          id
          primaryChargeType {
            id
            chargeRuleSet {
              displayName
              chargeRules(first: 1) {
                edges {
                  node {
                    id
                    price
                  }
                }
              }
            }
          }
        }
      }
    }
    orders(active: true) {
      edges {
        node {
          id
          status
          total
          totalTaxes
        }
      }
    }
  }
`;
